import { useEffect, useMemo, useRef, useState } from "react";
import { splitDuration } from "@/shared/birthday";
function useCountdown(targetIso, serverNowIso, onArrive) {
  const skewRef = useRef(null);
  if (skewRef.current === null && serverNowIso) {
    const serverNow = new Date(serverNowIso).getTime();
    if (Number.isFinite(serverNow)) skewRef.current = serverNow - Date.now();
  }
  const skew = skewRef.current ?? 0;
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [state, setState] = useState(
    () => splitDuration(Date.now() + skew, target)
  );
  const arrivedRef = useRef(false);
  const arriveRef = useRef(onArrive);
  arriveRef.current = onArrive;
  useEffect(() => {
    arrivedRef.current = false;
    let timer;
    let cancelled = false;
    const tick = () => {
      const now = Date.now() + skew;
      const next = splitDuration(now, target);
      setState(next);
      if (next.elapsed && !arrivedRef.current) {
        arrivedRef.current = true;
        arriveRef.current?.();
      }
      if (cancelled) return;
      if (next.elapsed) {
        return;
      }
      const delay = 1e3 - now % 1e3 + 8;
      timer = window.setTimeout(tick, delay);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [target, skew]);
  return state;
}
const pad = (n) => String(n).padStart(2, "0");
function RollingNumber({ value, reduced }) {
  const [slot, setSlot] = useState({
    cur: value,
    prev: null,
    key: 0
  });
  useEffect(() => {
    setSlot((s) => s.cur === value ? s : { cur: value, prev: s.cur, key: s.key + 1 });
  }, [value]);
  const clearPrev = () => setSlot((s) => s.prev === null ? s : { ...s, prev: null });
  useEffect(() => {
    if (slot.prev === null) return;
    const id = window.setTimeout(clearPrev, 400);
    return () => window.clearTimeout(id);
  }, [slot.prev, slot.key]);
  const chars = Math.max(2, value.length);
  if (reduced) {
    return <span className="count-num gold-text">{value}</span>;
  }
  return <span className="count-num">
      <span className="digit-window" style={{ minWidth: `${chars}ch` }}>
        {slot.prev !== null && <span
    key={`out-${slot.key}`}
    className="digit-slot digit-out gold-text"
    aria-hidden="true"
    onAnimationEnd={clearPrev}
  >
            {slot.prev}
          </span>}
        <span key={`in-${slot.key}`} className="digit-slot digit-in gold-text">
          {slot.cur}
        </span>
      </span>
    </span>;
}
function Countdown({
  targetIso,
  serverNowIso,
  onArrive,
  compact = false,
  reduced = false,
  className = ""
}) {
  const d = useCountdown(targetIso, serverNowIso, onArrive);
  const cells = [
    [String(d.days), "days"],
    [pad(d.hours), "hours"],
    [pad(d.minutes), "minutes"],
    [pad(d.seconds), "seconds"]
  ];
  return <div
    className={className}
    role="timer"
    aria-live="off"
    aria-label={`Countdown: ${d.days} days, ${d.hours} hours, ${d.minutes} minutes, ${d.seconds} seconds`}
  >
      <div
    className={`flex items-stretch justify-center ${compact ? "gap-1" : "gap-1.5 sm:gap-3"}`}
  >
        {cells.map(([value, label], i) => <div key={label} className="flex items-stretch">
            <div
    className="count-cell flex flex-col items-center justify-center"
    style={{ minWidth: compact ? "3.6rem" : void 0 }}
  >
              <RollingNumber value={value} reduced={reduced} />
              <span className="count-label">{label}</span>
            </div>
            {i < cells.length - 1 && <span
    aria-hidden="true"
    className="count-sep self-center px-0.5 text-lg text-[color:var(--gold-dim)] opacity-35 sm:px-1"
  >
                :
              </span>}
          </div>)}
      </div>
    </div>;
}
function Typewriter({
  text,
  speed = 26,
  startDelay = 300,
  onDone,
  reducedMotion,
  className = "",
  as: Tag = "div"
}) {
  const [shown, setShown] = useState(reducedMotion ? text.length : 0);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    doneRef.current = false;
    if (reducedMotion) {
      setShown(text.length);
      doneRef.current = true;
      onDoneRef.current?.();
      return;
    }
    setShown(0);
    let i = 0;
    let timer;
    let cancelled = false;
    const step = () => {
      if (cancelled) return;
      i += 1;
      setShown(i);
      if (i >= text.length) {
        if (!doneRef.current) {
          doneRef.current = true;
          onDoneRef.current?.();
        }
        return;
      }
      const ch = text[i - 1];
      const pause = ch === "." || ch === "!" || ch === "?" ? speed * 5 : ch === "," ? speed * 2.5 : speed;
      timer = window.setTimeout(step, pause);
    };
    timer = window.setTimeout(step, startDelay);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [text, speed, startDelay, reducedMotion]);
  const visible = text.slice(0, shown);
  const typing = shown < text.length;
  const lines = visible.split("\n");
  return <Tag className={`${className} ${typing ? "caret" : ""}`}>
      {
    /* Preserve the author's paragraph breaks without dangerouslySetInnerHTML. */
  }
      {lines.map((line, i) => <span key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </span>)}
      {typing && <span className="sr-only" aria-live="off">
          {" "}
        </span>}
    </Tag>;
}
export {
  Countdown,
  Typewriter,
  useCountdown
};
